import { VehicleMedia } from "@alba-cars/common-modules";
import dynamic from "next/dynamic";

import OptimizedImage from "@/components/common/images/OptimizedImage";
import { DEFAULT_PLACEHOLDER } from "@/utils/constants";

import "swiper/css";
import { ArrowRight } from "lucide-react";

interface MobileSwiperProps {
  images: VehicleMedia[];
  altText: string;
  onImageClick: (index: number) => void;
  showRamadanBanner?: boolean;
  hasVerticalVideo?: boolean;
  onVerticalVideoOpenAction?: () => void;
  isReserved?: boolean;
  isCarOfTheDay?: boolean;
  imageObjectFit?: "cover" | "contain";
}

const DynamicSwiper = dynamic(
  async () => {
    const SwiperReact = await import("swiper/react");
    const { useState, useRef } = await import("react");
    const { useTranslations } = await import("next-intl");
    const { Smartphone } = await import("lucide-react");
    const VideoPlayer = (await import("@/components/common/VideoPlayer")).default;
    const { formatImageUrl } = await import("@/lib/utils");
    const { DEFAULT_PLACEHOLDER } = await import("@/utils/constants");
    const { extractHorizontalVideo, getImageMedia } = await import("./videoUtils");

    return function MobileSwiperInner({
      images,
      altText,
      onImageClick,
      hasVerticalVideo = false,
      onVerticalVideoOpenAction,
      isReserved = false,
      isCarOfTheDay = false,
      imageObjectFit = "cover",
    }: MobileSwiperProps) {
      const tGallery = useTranslations("CarDetails.gallery");
      const [activeIndex, setActiveIndex] = useState(0);
      const videoRef = useRef<HTMLVideoElement>(null);
      const youtubeIframeRef = useRef<HTMLIFrameElement | null>(null);
      const swiperRef = useRef<any>(null);
      const edgeSwipeStartX = useRef<number | null>(null);

      const horizontalVideo = extractHorizontalVideo(images as any[]);
      const imageOnlyMedia = getImageMedia(images as any[]);
      const totalSlides = (horizontalVideo ? 1 : 0) + imageOnlyMedia.length;
      const cdnBase = process.env.NEXT_PUBLIC_S3_URL ?? "";
      const shouldShowVerticalTrigger = hasVerticalVideo && !(horizontalVideo && activeIndex === 0);
      const isContainFit = imageObjectFit === "contain";
      const slideFrameClass = isContainFit
        ? "relative aspect-[3/4] max-h-[70vh] overflow-hidden rounded-2xl bg-neutral-100"
        : "relative aspect-[4/3] overflow-hidden rounded-2xl";
      const imageFitClass = isContainFit ? "object-contain object-center" : "object-cover object-center";
      const pauseHorizontalVideo = () => {
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
        }

        youtubeIframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({
            event: "command",
            func: "pauseVideo",
            args: [],
          }),
          "*",
        );
      };

      const handleIndicatorClick = (index: number) => {
        if (!swiperRef.current) {
          return;
        }

        swiperRef.current.slideTo(index);
        setActiveIndex(index);
      };

      const handleEdgeSwipeStart = (event: React.TouchEvent<HTMLDivElement>) => {
        edgeSwipeStartX.current = event.touches[0]?.clientX ?? null;
      };

      const handleEdgeSwipeEnd = (event: React.TouchEvent<HTMLDivElement>) => {
        if (!swiperRef.current || edgeSwipeStartX.current === null) {
          return;
        }

        const endX = event.changedTouches[0]?.clientX ?? edgeSwipeStartX.current;
        const deltaX = endX - edgeSwipeStartX.current;
        edgeSwipeStartX.current = null;

        if (Math.abs(deltaX) < 24) {
          return;
        }

        if (deltaX < 0) {
          swiperRef.current.slideNext();
          return;
        }

        swiperRef.current.slidePrev();
      };

      // Show max 3 indicators, centered around active
      const maxDots = 3;
      const getVisibleDots = () => {
        if (totalSlides <= maxDots) {
          return Array.from({ length: totalSlides }, (_, i) => i);
        }
        let start = activeIndex - 1;
        if (start < 0) start = 0;
        if (start + maxDots > totalSlides) start = totalSlides - maxDots;
        return Array.from({ length: maxDots }, (_, i) => start + i);
      };

      const visibleDots = getVisibleDots();

      return (
        <div className="relative lg:hidden">
          <div className="relative overflow-hidden rounded-2xl">
            {isReserved && (
              <div className="absolute left-3 top-3 z-50 flex flex-wrap items-center gap-2">
                <div className="inter rounded-lg px-3 py-1 text-center text-xs font-semibold bg-primary text-white">
                  {isCarOfTheDay ? tGallery("carOfTheDay") : tGallery("reserved")}
                </div>
              </div>
            )}
            <SwiperReact.Swiper
              onSwiper={(swiper: any) => {
                swiperRef.current = swiper;
              }}
              slidesPerView={1}
              spaceBetween={0}
              loop={false}
              touchRatio={1}
              simulateTouch={true}
              allowTouchMove={true}
              threshold={10}
              onSlideChange={(swiper) => {
                const nextIndex = swiper.activeIndex;
                const prevIndex = swiper.previousIndex;

                if (horizontalVideo && prevIndex === 0 && nextIndex !== 0) {
                  pauseHorizontalVideo();
                }

                setActiveIndex(nextIndex);
              }}
              className="overflow-hidden rounded-2xl"
            >
              {/* ── Video slide (first) — disabled via extractHorizontalVideo ── */}
              {horizontalVideo && (
                <SwiperReact.SwiperSlide key="mobile-video-slide">
                  <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-black">
                    <VideoPlayer
                      s3Url={horizontalVideo.src}
                      cdnBase={cdnBase}
                      videoRef={videoRef}
                      youtubeIframeRef={youtubeIframeRef}
                      startLevel={2}
                      capLevelToPlayerSize={false}
                      className="h-full w-full object-contain"
                    />
                    {/* Keep center area clickable for YouTube controls; edge zones preserve swipe navigation. */}
                    <div
                      aria-hidden={true}
                      className="absolute left-0 top-0 z-10 h-full w-1/5 touch-pan-y"
                      onTouchStart={handleEdgeSwipeStart}
                      onTouchEnd={handleEdgeSwipeEnd}
                    />
                    <div
                      aria-hidden={true}
                      className="absolute right-0 top-0 z-10 h-full w-1/5 touch-pan-y"
                      onTouchStart={handleEdgeSwipeStart}
                      onTouchEnd={handleEdgeSwipeEnd}
                    />
                    {/* Custom play/pause button */}
                    {/* <button
                      onClick={handleVideoToggle}
                      className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/60"
                      aria-label={isPlaying ? "Pause video" : "Play video"}
                    >
                      {isPlaying ? (
                        <Pause color="white" size={16} fill="white" />
                      ) : (
                        <Play color="white" size={16} fill="white" />
                      )}
                    </button> */}
                  </div>
                </SwiperReact.SwiperSlide>
              )}

              {/* ── Image slides ── */}
              {imageOnlyMedia.map((image, idx) => (
                <SwiperReact.SwiperSlide key={image.id ?? idx}>
                  <div className={slideFrameClass}>
                    <OptimizedImage
                      src={formatImageUrl(image.mediaUrl) ?? DEFAULT_PLACEHOLDER}
                      alt={altText}
                      className={`h-full w-full ${imageFitClass}`}
                      objectFit={imageObjectFit}
                      imageLayout="custom"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      loading={idx < 3 ? "eager" : "lazy"}
                      priority={idx < 3}
                      fetchPriority={idx < 3 ? "high" : "auto"}
                      onClick={() => onImageClick(horizontalVideo ? idx + 1 : idx)}
                      width={300}
                      height={0}
                      maxWidth={800}
                      placeholder={"blur"}
                      blurDataURL={DEFAULT_PLACEHOLDER}
                    />
                  </div>
                </SwiperReact.SwiperSlide>
              ))}
            </SwiperReact.Swiper>

            {/* Vertical video trigger — disabled via extractVerticalVideo / Gallery props */}
            {shouldShowVerticalTrigger && (
              <button
                type="button"
                className="absolute bottom-3 right-3 z-20 flex animate-shake items-center gap-2 rounded-full bg-black/70 px-3 py-1.5 font-inter text-xs font-semibold text-white [animation-duration:2.8s] [animation-iteration-count:infinite] [animation-timing-function:ease-in-out]"
                // className="absolute bottom-3 right-3 z-20 flex items-center gap-2 rounded-full bg-black/70 px-3 py-2.5 font-inter text-xs font-semibold text-white"
                onClick={onVerticalVideoOpenAction}
                aria-label="Open vertical video"
              >
                <Smartphone size={13} />
                {tGallery("playVideo")}
              </button>
            )}
          </div>

          {/* ── Indicators outside slider ── */}
          {totalSlides > 1 && (
            <div className="relative mt-3 flex items-center justify-center">
              <div className="flex items-center gap-1.5">
                {visibleDots.map((dotIndex) => (
                  <button
                    type="button"
                    key={dotIndex}
                    onClick={() => handleIndicatorClick(dotIndex)}
                    aria-label={`Go to slide ${dotIndex + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      dotIndex === activeIndex ? "w-6 bg-primary" : "w-1.5 bg-gray-300"
                    }`}
                  />
                ))}
              </div>
              <span className="pointer-events-none absolute left-1/2 flex translate-x-8 items-center gap-1 text-xs font-semibold text-primary">
                {tGallery("swipeForPhotos")}
              </span>
            </div>
          )}
        </div>
      );
    };
  },
  {
    ssr: false,
    loading: () => <MobileSwiperLoader />,
  },
);

const MobileSwiper: React.FC<MobileSwiperProps> = ({
  images,
  altText,
  onImageClick,
  showRamadanBanner,
  hasVerticalVideo,
  onVerticalVideoOpenAction,
  isReserved,
  isCarOfTheDay = false,
  imageObjectFit = "cover",
}) => {
  return (
    <DynamicSwiper
      images={images}
      altText={altText}
      onImageClick={onImageClick}
      showRamadanBanner={showRamadanBanner}
      hasVerticalVideo={hasVerticalVideo}
      onVerticalVideoOpenAction={onVerticalVideoOpenAction}
      isReserved={isReserved}
      isCarOfTheDay={isCarOfTheDay}
      imageObjectFit={imageObjectFit}
    />
  );
};

const MobileSwiperLoader = () => {
  return (
    <div className="relative lg:hidden">
      <div className="overflow-hidden rounded-2xl">
        <div className="relative aspect-[4/3] overflow-hidden rounded-2xl">
          <OptimizedImage
            src={DEFAULT_PLACEHOLDER}
            alt={"Loading..."}
            className="h-full w-full object-cover object-center"
            imageLayout="custom"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            loading="eager"
            priority={true}
            fetchPriority="high"
            width={300}
            height={0}
            maxWidth={800}
            placeholder={"blur"}
            blurDataURL={DEFAULT_PLACEHOLDER}
          />
        </div>
      </div>
      <div className="relative mt-3 flex items-center justify-center">
        <div className="flex items-center gap-1.5">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={`h-1.5 rounded-full bg-gray-300 ${i === 0 ? "w-6" : "w-1.5"}`} />
          ))}
        </div>
        <div className="absolute left-1/2 h-2 w-16 translate-x-8 rounded bg-gray-200" />
      </div>
    </div>
  );
};

export default MobileSwiper;
